import React from 'react';
import { FormattedMessage } from 'react-intl';
import { AppState } from 'src/AppState';
import AppStateView from 'src/AppStateView';
import { EasyClaimAppState } from 'src/easyclaim.api.client.typescript/src';
import styled from 'styled-components';

import theme from '../easyclaim.app.theme/theme';

import { Circle } from './common/Circle';

export class Steps extends React.Component<AppState> {
	private isStepCurrent(
		currentlyRenderedScreenConfig: AppStateView,
		currentAppState: EasyClaimAppState,
		steps: EasyClaimAppState[]
	) {
		const lastStepWithIcon = this.getLastStepWithIcon(
			currentAppState,
			steps,
			this.props.appStateViews
		);

		const isItCurrentScreenIconRendering =
			currentlyRenderedScreenConfig.appState === this.props.screen;

		return (
			isItCurrentScreenIconRendering || lastStepWithIcon === currentlyRenderedScreenConfig.appState
		);
	}

	private getIconColor(
		currentlyRenderedScreenConfig: AppStateView,
		currentAppState: EasyClaimAppState,
		steps: EasyClaimAppState[]
	) {
		return this.isStepCurrent(currentlyRenderedScreenConfig, currentAppState, steps)
			? theme.colors.icon.active.secondary
			: theme.colors.icon.secondary;
	}

	private getCurrentStepClassName(
		currentlyRenderedScreenConfig: AppStateView,
		currentAppState: EasyClaimAppState,
		steps: EasyClaimAppState[]
	) {
		return this.isStepCurrent(currentlyRenderedScreenConfig, currentAppState, steps)
			? 'current-visible-steps-circle'
			: '';
	}

	private getCurrentStepContainerClassName(
		currentlyRenderedScreenConfig: AppStateView,
		currentAppState: EasyClaimAppState,
		steps: EasyClaimAppState[]
	) {
		return this.isStepCurrent(currentlyRenderedScreenConfig, currentAppState, steps)
			? 'current-step-button-container'
			: 'step-button-container';
	}

	private getLastStepWithIcon(
		candidateStep: EasyClaimAppState,
		frontendSteps: EasyClaimAppState[],
		appStateConfig: AppStateView[]
	): EasyClaimAppState {
		if (!candidateStep) {
			return frontendSteps[0];
		}
		if (
			appStateConfig.find((asc) => asc.appState === candidateStep && !!asc.iconRenderer === true)
		) {
			return candidateStep;
		} else {
			const candidateScreenIndex = frontendSteps.findIndex((fs) => fs === candidateStep);

			if (candidateScreenIndex <= 0) {
				return frontendSteps[0];
			}
			const prevScreen = frontendSteps[candidateScreenIndex - 1];

			return this.getLastStepWithIcon(prevScreen, frontendSteps, appStateConfig);
		}
	}

	private allowClickOnSteps = (currentlyRenderedScreenConfig: AppStateView) => {
		return currentlyRenderedScreenConfig.allowClickOnSteps
			? currentlyRenderedScreenConfig.allowClickOnSteps(this.props)
			: true;
	};

	render() {
		if (
			!this.props.screen ||
			!this.props.easyClaimCase ||
			!this.props.easyClaimCase._configuration ||
			!this.props.easyClaimCase._configuration.frontendSteps
		) {
			return;
		}

		const currentAppState = this.props.screen;
		const frontendSteps = this.props.easyClaimCase._configuration.frontendSteps.map(
			(fs) => fs.appState!
		);

		const stepsWithIcons = this.props.appStateViews.filter(
			(s) =>
				!!s.iconRenderer &&
				!!s.iconRenderer(
					this.props,
					'1.25rem',
					s.appState === this.props.screen
						? theme.colors.icon.active.primary
						: theme.colors.icon.primary
				)
		);
		const visibleSteps = stepsWithIcons
			.filter(
				(s) => s.appState !== EasyClaimAppState.Feedback && frontendSteps.includes(s.appState)
			)
			.sort((a, b) =>
				frontendSteps.indexOf(a.appState) < frontendSteps.indexOf(b.appState) ? -1 : 1
			);

		return (
			<div className="menu-container-steps">
				{visibleSteps.map((s, i) => (
					<div
						key={s.appState}
						className={`${this.getCurrentStepContainerClassName(s, currentAppState, frontendSteps)}`}
					>
						<CircleButton
							className={`visible-steps-circle ${this.getCurrentStepClassName(s, currentAppState, frontendSteps)}`}
							key={i}
							backgroundColor={this.getIconColor(s, currentAppState, frontendSteps)}
							borderColor={theme.colors.icon.primary}
							onClick={() => {
								if (this.allowClickOnSteps(s)) {
									const newState = Object.assign({}, this.props, { screen: s.appState });

									void this.props.setAppState(newState);
								}
							}}
						>
							{s.iconRenderer!(
								this.props,
								'1.25rem',
								s.appState === currentAppState
									? theme.colors.icon.active.primary
									: theme.colors.icon.primary
							)}
						</CircleButton>
						<div className="step-button-text">
							<FormattedMessage id={`menuSteps.stepName.${s.appState.toString()}`} />
						</div>
					</div>
				))}
			</div>
		);
	}
}

const CircleButton = styled(Circle)`
	transition:
		background-color ${(p) => p.theme.transitions.duration},
		box-shadow ${(p) => p.theme.transitions.duration};
	-webkit-tap-highlight-color: rgba(0, 0, 0, 0);
`;
